import CustomDropdown from '../reusable/dropdown';
import DeletePipelineModal from './modal/delete_pipeline';
import DuplicatePipeline from './modal/duplicate_pipeline';
import { settingArhievedList } from '../../redux/slices/deal';
import { toggleBulkEditMode } from '../../redux/slices/pipeline';
import UpdatePipelineModal from './modal/update_pipeline';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, SelectChangeEvent } from '@mui/material';
import { ContentCopyOutlined, EditNoteOutlined, EditOutlined, Inventory2Outlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: PipelineActionDropdown
 * @paragraph: A dropdown component that provides various actions related to pipeline management.
 * Includes options to edit, duplicate, and manage archived deals for pipelines.
 */
const PipelineActionDropdown = () => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions such as hiding the error.
     * showError/showDelete/showDuplicate: React state for visibility of modals
     * moreItems: items for dropdown static.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [showDuplicate, setShowDuplicate] = useState<boolean>(false);
    const totalDeals = selectedPipeline?.pipeline_stages?.reduce((sum, stage) => {
        return sum + (stage.deals?.length || 0);
    }, 0) || 0;

    const disableBulkEdit = !(totalDeals >= 2);
    const moreItems = [
        { label: 'More', value: 'more' },
        { label: 'Edit Pipeline', value: 'edit', icon: <EditOutlined fontSize="small" /> },
        { label: 'Duplicate Pipeline', value: 'duplicate', icon: <ContentCopyOutlined fontSize="small" /> },
        { label: 'Archived Deals', value: 'archieved', icon: <Inventory2Outlined fontSize="small" /> },
        { label: 'Bulk Edit', value: 'bulk', icon: <EditNoteOutlined fontSize="small" />, disabled: disableBulkEdit }
    ];

    /**
     * @heading3: Functions
     * @heading4: handleMoreSelection
     * @paragraph: Handles the selection of dropdown options and performs the corresponding action.
     * @param: {SelectChangeEvent<unknown>} event - The dropdown change event.
     */
    const handleMoreSelection = (event: SelectChangeEvent<unknown>) => {
        const value = event.target.value as string;
        if (value === 'edit') {
            setShowEdit(true);
        } else if (value === 'duplicate') {
            setShowDuplicate(true);
        } else if (value === 'archieved') {
            dispatch(settingArhievedList(true));
        } else if (value === 'bulk') {
            dispatch(toggleBulkEditMode(true));
        }
    };

    /**
     * @heading4: onDeletePressHandler
     * @paragraph: Handles the delete action by showing the `DeletePipelineModal`.
     */
    const onDeletePressHandler = () => {
        setShowEdit(false);
        setShowDelete(true);
    };


    return <Box>
        {/* Dropdown for selecting pipeline actions */}
        <CustomDropdown
            options={moreItems}
            onChange={handleMoreSelection}
            error={false}
            value="more"
            sx={{
                width: 'fit-content',
                mb: 0
            }}
        />
        {/* Modals for different pipeline actions */}
        {showEdit && <UpdatePipelineModal open={showEdit} onClose={() => setShowEdit(false)} onDelete={onDeletePressHandler} />}
        {showDelete && <DeletePipelineModal open={showDelete} onClose={() => setShowDelete(false)} />}
        {showDuplicate && <DuplicatePipeline open={showDuplicate} onClose={() => setShowDuplicate(false)} />}
    </Box>;
};

export default PipelineActionDropdown;